import { TILE_SIZE } from '@game/protocol';

function tileCenter(tile: number): number {
  return tile * TILE_SIZE + TILE_SIZE / 2;
}

/**
 * Posição visual de uma entidade, interpolada entre tiles (PRD §3.2).
 *
 * O servidor só conhece coordenadas inteiras: a entidade está no tile 10 ou no 11,
 * nunca no meio. Renderizar isso cru daria um salto seco a cada passo. Como o Snapshot
 * carrega a duração do passo, dá para deslizar exatamente por esse tempo e a
 * movimentação fica contínua sem que o cliente invente nada.
 */
export class EntityView {
  gridX: number;
  gridY: number;

  renderX: number;
  renderY: number;

  private fromX: number;
  private fromY: number;
  private elapsedMs = 0;
  private durationMs = 0;

  constructor(gridX: number, gridY: number) {
    this.gridX = gridX;
    this.gridY = gridY;
    this.renderX = tileCenter(gridX);
    this.renderY = tileCenter(gridY);
    this.fromX = this.renderX;
    this.fromY = this.renderY;
  }

  get isMoving(): boolean {
    return this.durationMs > 0;
  }

  /**
   * `stepMs` a zero significa reposicionamento, não caminhada: spawn, entrada na Área
   * de Interesse ou correção de mispredição. Nesses casos deslizar seria mentira visual.
   */
  moveTo(gridX: number, gridY: number, stepMs: number): void {
    if (gridX === this.gridX && gridY === this.gridY) return;

    this.gridX = gridX;
    this.gridY = gridY;

    if (stepMs <= 0) {
      this.snap();
      return;
    }

    // Parte da posição renderizada, não do tile de origem: se o passo anterior ainda
    // não terminou, encadear daqui evita o solavanco de voltar meio tile.
    this.fromX = this.renderX;
    this.fromY = this.renderY;
    this.elapsedMs = 0;
    this.durationMs = stepMs;
  }

  snap(): void {
    this.renderX = tileCenter(this.gridX);
    this.renderY = tileCenter(this.gridY);
    this.fromX = this.renderX;
    this.fromY = this.renderY;
    this.durationMs = 0;
    this.elapsedMs = 0;
  }

  update(dtMs: number): void {
    if (this.durationMs <= 0) return;

    this.elapsedMs += dtMs;
    const t = Math.min(1, this.elapsedMs / this.durationMs);

    const targetX = tileCenter(this.gridX);
    const targetY = tileCenter(this.gridY);

    this.renderX = this.fromX + (targetX - this.fromX) * t;
    this.renderY = this.fromY + (targetY - this.fromY) * t;

    if (t >= 1) {
      this.durationMs = 0;
      this.elapsedMs = 0;
      this.fromX = this.renderX;
      this.fromY = this.renderY;
    }
  }
}
